/**
 * Registry-driven action surfaces (DESIGN.md §18): toolbar buttons, the "…" overflow menu and
 * the phone action sheet. They render `ResolvedAction`s from `resolveActions`, so a label, icon,
 * shortcut or predicate is never defined twice. The context menu (`ContextMenuArea`) renders the
 * same `ActionMenuItems`.
 */
import { MoreHorizontalIcon } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../components/badge';
import { Button } from '../components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from '../components/dropdown-menu';
import { Kbd } from '../components/misc';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';
import { BREAKPOINTS, useMediaQuery } from '../hooks/use-media-query';
import { cn } from '../lib/utils';
import { useUiStrings } from '../strings';
import { groupActions, labelText, type ResolvedAction } from './registry';
import { useRunAction } from './runner';
import { shortcutText } from './shortcut-label';

/** Label, shortcut text and icon of a resolved action in the current language. */
function usePresentation() {
  const strings = useUiStrings();
  return <T,>(entry: ResolvedAction<T>) => ({
    key: entry.action.id,
    label: labelText(strings.translate, entry.label),
    shortcut: entry.planned ? undefined : shortcutText(entry.action.shortcut, strings.keys),
    Icon: entry.action.icon,
    planned: entry.planned ? strings.planned : undefined,
  });
}

/** Dropdown items (with group separators) for the overflow menu and the context menu. */
export function ActionMenuItems<T>({ actions }: { actions: readonly ResolvedAction<T>[] }) {
  const present = usePresentation();
  const run = useRunAction();
  return groupActions(actions).map((group, index) => (
    <div key={group[0]!.action.id} role="none" className="contents">
      {index > 0 && <DropdownMenuSeparator />}
      {group.map((entry) => {
        const { key, label, shortcut, Icon, planned } = present(entry);
        return (
          <DropdownMenuItem
            key={key}
            disabled={!entry.enabled}
            variant={entry.action.danger ? 'destructive' : 'default'}
            className={cn(entry.action.defaultGesture && entry.context.source === 'context' && 'font-semibold')}
            onSelect={() => run(entry)}
          >
            {Icon && <Icon aria-hidden />}
            <span className="flex-1">{label}</span>
            {planned && <Badge variant="muted">{planned}</Badge>}
            {shortcut && <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>}
          </DropdownMenuItem>
        );
      })}
    </div>
  ));
}

export interface ActionSheetProps<T> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Names the target, e.g. the subject or "3 messages"; screen readers get `actions` otherwise. */
  title?: string;
  actions: readonly ResolvedAction<T>[];
}

/** Phone variant of the action menus (< md): a bottom sheet with large touch targets. */
export function ActionSheet<T>({ open, onOpenChange, title, actions }: ActionSheetProps<T>) {
  const strings = useUiStrings();
  const present = usePresentation();
  const run = useRunAction();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-sheet pb-2">
        <SheetHeader className="pb-2">
          <SheetTitle className={cn('truncate pr-8', !title && 'sr-only')}>{title ?? strings.actions}</SheetTitle>
        </SheetHeader>
        <div role="menu" aria-label={title ?? strings.actions} className="flex flex-col overflow-y-auto px-2">
          {groupActions(actions).map((group, index) => (
            <div key={group[0]!.action.id} role="none" className={cn('flex flex-col', index > 0 && 'mt-1 border-t pt-1')}>
              {group.map((entry) => {
                const { key, label, Icon, planned } = present(entry);
                return (
                  <button
                    key={key}
                    type="button"
                    role="menuitem"
                    disabled={!entry.enabled}
                    onClick={() => {
                      onOpenChange(false);
                      run(entry);
                    }}
                    className={cn(
                      'flex h-12 items-center gap-3 rounded-md px-3 text-left text-sm outline-none hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground',
                      entry.action.danger && 'text-destructive [&_svg]:text-destructive',
                    )}
                  >
                    {Icon && <Icon aria-hidden />}
                    <span className="flex-1">{label}</span>
                    {planned && <Badge variant="muted">{planned}</Badge>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export interface ActionOverflowMenuProps<T> {
  actions: readonly ResolvedAction<T>[];
  /** Accessible name of the button (default "More actions"). */
  label?: string;
  /** Sheet title on phones. */
  title?: string;
  align?: 'start' | 'end';
  size?: 'icon' | 'icon-sm';
  className?: string;
}

/** The "…" button: a dropdown on larger screens, the action sheet on phones. Renders nothing without actions. */
export function ActionOverflowMenu<T>({ actions, label, title, align = 'end', size = 'icon', className }: ActionOverflowMenuProps<T>) {
  const strings = useUiStrings();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const [sheetOpen, setSheetOpen] = useState(false);
  if (actions.length === 0) return null;
  const button = (
    <Button variant="ghost" size={size} aria-label={label ?? strings.moreActions} className={className} onClick={isDesktop ? undefined : () => setSheetOpen(true)}>
      <MoreHorizontalIcon />
    </Button>
  );
  if (!isDesktop) {
    return (
      <>
        {button}
        <ActionSheet open={sheetOpen} onOpenChange={setSheetOpen} title={title} actions={actions} />
      </>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} aria-label={label ?? strings.moreActions}>
        <ActionMenuItems actions={actions} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface ActionButtonsProps<T> {
  actions: readonly ResolvedAction<T>[];
  /** `icon`: ghost icon buttons with a tooltip (reading pane); `label`: outline buttons with text (bulk bars). */
  variant?: 'icon' | 'label';
  /** Emphasize the default action while keeping one registry for every surface. */
  primaryActionId?: string;
}

/** Toolbar buttons for the `toolbar` surface. */
export function ActionButtons<T>({ actions, variant = 'icon', primaryActionId }: ActionButtonsProps<T>) {
  const present = usePresentation();
  const run = useRunAction();
  return actions.map((entry) => {
    const { key, label, shortcut, Icon, planned } = present(entry);
    const hint = planned ? `${label} · ${planned}` : label;
    if (variant === 'label') {
      return (
        <Button key={key} variant={entry.action.danger ? 'destructive' : entry.action.id === primaryActionId ? 'default' : 'outline'} disabled={!entry.enabled} title={planned ? hint : undefined} onClick={() => run(entry)}>
          {Icon && <Icon aria-hidden />}
          {label}
        </Button>
      );
    }
    return (
      <Tooltip key={key}>
        <TooltipTrigger asChild>
          {/* The wrapper keeps the tooltip working on disabled (planned) buttons. */}
          <span className="inline-flex">
            <Button variant="ghost" size="icon" aria-label={label} disabled={!entry.enabled} onClick={() => run(entry)}>
              {Icon && <Icon />}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="flex items-center gap-2">
          {hint}
          {shortcut && <Kbd>{shortcut}</Kbd>}
        </TooltipContent>
      </Tooltip>
    );
  });
}
